import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { POC } from "@/components/POC";
import { AdminLogin } from "@/components/AdminLogin";
import { AdminLeads } from "@/components/AdminLeads";
import { AdminSettings } from "@/components/AdminSettings";
import { AdminCMS } from "@/components/AdminCMS";
import { AdminCMSEditor } from "@/components/AdminCMSEditor";
import { Toaster } from "@/components/ui/sonner";
import { TrackingScripts } from "@/components/TrackingScripts";
import { ScrollToTop } from "@/components/ScrollToTop";

// Pages
import { HomePage } from "@/pages/HomePage";
import { NettoyagePage } from "@/pages/NettoyagePage";
import { ConciergeriePage } from "@/pages/ConciergeriePage";
import { RenovationPage } from "@/pages/RenovationPage";
import { MarketingPage } from "@/pages/MarketingPage";
import { ImmoPage } from "@/pages/ImmoPage";
import { ContactPage } from "@/pages/ContactPage";
import { MontagePage } from "@/pages/MontagePage";

// Nettoyage sub-pages
import { NettoyageCanapePage } from "@/pages/nettoyage/NettoyageCanapePage";
import { NettoyageTerrassePage } from "@/pages/nettoyage/NettoyageTerrassePage";
import { NettoyageVitresPage } from "@/pages/nettoyage/NettoyageVitresPage";
import { NettoyageFinDeChantierPage } from "@/pages/nettoyage/NettoyageFinDeChantierPage";
import { NettoyageApresBailPage } from "@/pages/nettoyage/NettoyageApresBailPage";

// Montage sub-pages
import { MontageMeublesPage } from "@/pages/montage/MontageMeublesPage";
import { MontageCuisinePage } from "@/pages/montage/MontageCuisinePage";
import { TransportPage } from "@/pages/montage/TransportPage";
import { HandyManPage } from "@/pages/montage/HandyManPage";

// Renovation sub-pages
import { RenovationCuisinePage } from "@/pages/renovation/RenovationCuisinePage";
import { RenovationSalleDeBainPage } from "@/pages/renovation/RenovationSalleDeBainPage";
import { RenovationCompletePage } from "@/pages/renovation/RenovationCompletePage";
import { RenovationSubServiceCityPage } from "@/pages/RenovationSubServiceCityPage";

// SEO City Pages (hidden from menu)
import { SEOCityPage } from "@/pages/SEOCityPage";

// Legal Pages
import { MentionsLegalesPage } from "@/pages/MentionsLegalesPage";
import { PolitiqueConfidentialitePage } from "@/pages/PolitiqueConfidentialitePage";
import { CGVPage } from "@/pages/CGVPage";

// CMS Dynamic Pages
import { CMSPage } from "@/pages/CMSPage";

const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="mx-auto w-full max-w-7xl">
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <TrackingScripts />
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/nettoyage" element={<NettoyagePage />} />
            <Route path="/nettoyage/canape" element={<NettoyageCanapePage />} />
            <Route path="/nettoyage/terrasse" element={<NettoyageTerrassePage />} />
            <Route path="/nettoyage/vitres" element={<NettoyageVitresPage />} />
            <Route path="/nettoyage/fin-de-chantier" element={<NettoyageFinDeChantierPage />} />
            <Route path="/nettoyage/apres-bail" element={<NettoyageApresBailPage />} />
            <Route path="/conciergerie" element={<ConciergeriePage />} />
            <Route path="/renovation" element={<RenovationPage />} />
            <Route path="/renovation/cuisine" element={<RenovationCuisinePage />} />
            <Route path="/renovation/salle-de-bain" element={<RenovationSalleDeBainPage />} />
            <Route path="/renovation/complete" element={<RenovationCompletePage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/immo" element={<ImmoPage />} />
            <Route path="/montage" element={<MontagePage />} />
            <Route path="/montage/meubles" element={<MontageMeublesPage />} />
            <Route path="/montage/cuisine" element={<MontageCuisinePage />} />
            <Route path="/montage/transport" element={<TransportPage />} />
            <Route path="/montage/handyman" element={<HandyManPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Legal Pages */}
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="/cgv" element={<CGVPage />} />
            
            {/* SEO City Pages - Hidden (not in menu) */}
            <Route path="/nettoyage/:city" element={<SEOCityPage service="nettoyage" />} />
            <Route path="/renovation/:city" element={<SEOCityPage service="renovation" />} />
            <Route path="/renovation/cuisine/:city" element={<RenovationSubServiceCityPage subService="cuisine" />} />
            <Route path="/renovation/salle-de-bain/:city" element={<RenovationSubServiceCityPage subService="salle-de-bain" />} />
            <Route path="/renovation/complete/:city" element={<RenovationSubServiceCityPage subService="complete" />} />
            <Route path="/montage/:city" element={<SEOCityPage service="montage" />} />
            <Route path="/conciergerie/:city" element={<SEOCityPage service="conciergerie" />} />
            <Route path="/immo/:city" element={<SEOCityPage service="immo" />} />
            <Route path="/marketing/:city" element={<SEOCityPage service="marketing" />} />
            
            {/* POC (test) */}
            <Route path="/poc" element={<POC />} />
            
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
            <Route path="/admin/cms" element={<AdminCMS />} />
            <Route path="/admin/cms/new" element={<AdminCMSEditor />} />
            <Route path="/admin/cms/edit/:pageId" element={<AdminCMSEditor />} />
            
            {/* CMS Dynamic Pages (must be after other routes) */}
            <Route path="/p/:slug" element={<CMSPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
